import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useSidebar } from "../lib/ui/sidebarStore";
import MobileMenu from "./MobileMenu";
import { apiLogout } from "../lib/api";
import { useBranding } from "../lib/branding";

/** ---- Types ---- */
type Plan = {
  label: string;
  description?: string;
  supports_fx?: boolean;
  supports_viban?: boolean;
};
type Trust = { id: number | string; status?: string } | null;
type ModuleStatus = {
  enabled?: boolean;
  reason?: string | null;
  dependency?: string | null;
};

type AnyUserData = {
  first_name?: string | null;
  last_name?: string | null;
  birthdate?: string | null;
  address_line1?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
} | null;

type AnyBusinessProfile = {
  company_name?: string | null;
  company_number?: string | null;
  vat_number?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
} | null;

type SessionData = {
  user?: any;
  plan?: Plan | null;
  trust?: Trust;
  hasAppliedTrust?: boolean;
  totalBalance?: number;
  modules?: Record<string, ModuleStatus>;
};

/** ---- Session fetch ---- */
async function fetchSession(): Promise<SessionData> {
  try {
    const res = await fetch("/xapi/me", { credentials: "include" });
    if (!res.ok) throw new Error("no session");
    const json = await res.json();
    return {
      user: json.user ?? json?.data?.user ?? undefined,
      plan: json.plan ?? json?.data?.plan ?? undefined,
      trust: json.trust ?? json?.data?.trust ?? null,
      hasAppliedTrust: typeof json.hasAppliedTrust === "boolean" ? json.hasAppliedTrust : undefined,
      modules: json.modules ?? json?.data?.modules ?? undefined,
      totalBalance:
        typeof json.totalBalance === "number"
          ? json.totalBalance
          : json?.data?.totalBalance ?? undefined,
    };
  } catch {
    return {};
  }
}

/** ---- Eligibility fetch ---- */
type EligibilityResp =
  | {
      ok: true;
      action: "dashboard" | "reapply" | "apply" | "not-eligible";
      trust?: { id: number; status: string } | null;
      eligibility: { latestStatus?: string | null; latestTrustId?: number | null };
    }
  | {
      ok: false;
      action: "blocked";
      trust?: { id: number; status: string } | null;
      eligibility: { latestStatus?: string | null; latestTrustId?: number | null };
    };

async function fetchEligibilityOnce(url: string): Promise<EligibilityResp | null> {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      if (res.status === 404 || res.status === 422) return null;
      if (res.status === 401) throw new Error("unauthorized");
      return null;
    }
    return (await res.json()) as EligibilityResp;
  } catch {
    return null;
  }
}
async function fetchTrustEligibility(): Promise<EligibilityResp | null> {
  return await fetchEligibilityOnce("/xapi/trust/eligibility");
}

/** ---- Helpers ---- */
function isPersonalIncomplete(user?: any): boolean {
  if (!user) return true;
  const t = (user.customer_type || "").toLowerCase();
  if (t === "business" || t === "company") return false;
  const d: AnyUserData = user.user_data ?? user.userData ?? {};
  return !(
    d?.first_name &&
    d?.last_name &&
    d?.birthdate &&
    d?.address_line1 &&
    d?.city &&
    d?.postal_code &&
    d?.country
  );
}

function isBusinessIncomplete(user?: any): boolean {
  if (!user) return false;
  const t = (user.customer_type || "").toLowerCase();
  if (!(t === "business" || t === "company")) return false;
  const b: AnyBusinessProfile = user.business_profile ?? user.businessProfile ?? {};
  return !(
    b?.company_name &&
    b?.company_number &&
    b?.vat_number &&
    b?.address &&
    b?.city &&
    b?.postal_code &&
    b?.country
  );
}

const APPLIED_STATUSES = new Set(["pending", "approved", "active", "processing"]);
function normalizeStatus(s?: string | null) {
  return (s ?? "").toString().trim().toLowerCase();
}

/** ---- AppShell ---- */
export default function AppShell() {
  const branding = useBranding();
  const { open, toggle, close } = useSidebar();
  const [session, setSession] = useState<SessionData>({});
  const [trustFromEligibility, setTrustFromEligibility] = useState<Trust>(null);
  const [eligibilityAction, setEligibilityAction] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const insideRegister = useMemo(() => {
    const p = location.pathname;
    return p.includes("/register/personal") || p.includes("/register/business");
  }, [location.pathname]);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Run onboarding/eligibility redirects ONLY on home/dashboard routes.
  useEffect(() => {
    const p = location.pathname;

    // Always hydrate minimal session; no redirects here.
    (async () => {
      try {
        const sess = await fetchSession();
        setSession((prev) => ({ ...prev, ...sess }));
      } catch {}
    })();

    // Only trigger global redirects on dashboard-ish routes
    const runRedirectsOn = new Set<string>(["/app", "/app/", "/app/dashboard", "/dashboard"]);
    const shouldRunGlobalRedirects = runRedirectsOn.has(p);
    if (!shouldRunGlobalRedirects) return;

    let alive = true;
    (async () => {
      const [sess, elig] = await Promise.all([fetchSession(), fetchTrustEligibility()]);
      if (!alive) return;

      let trust: Trust = sess.trust ?? null;
      const latestId = elig?.eligibility?.latestTrustId ?? null;
      const latestStatus = elig?.trust?.status ?? elig?.eligibility?.latestStatus ?? null;
      if (!trust && latestId) {
        trust = { id: latestId, status: latestStatus ?? undefined };
      }

      setTrustFromEligibility(trust);
      setEligibilityAction(elig?.action ?? null);
      setSession({ ...sess, trust });

      const user = sess.user;
      if (!user) {
        if (!insideRegister) navigate("/login", { replace: true });
        return;
      }

      if (!insideRegister) {
        const gate = (user as any)?.profile_gate;
        if (gate === "needs_business_profile") {
          navigate("/register/business?step=profile", { replace: true });
          return;
        }
        if (gate === "needs_personal_details") {
          navigate("/register/personal?step=details", { replace: true });
          return;
        }

        const needsBusiness = gate ? false : isBusinessIncomplete(user);
        const needsPersonal = gate ? false : isPersonalIncomplete(user);
        if (needsBusiness) {
          navigate("/register/business?step=profile", { replace: true });
          return;
        }
        if (needsPersonal) {
          navigate("/register/personal?step=details", { replace: true });
          return;
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [location.pathname, navigate, insideRegister]);

  const plan = session.plan ?? undefined;
  const user = session.user ?? undefined;
  const modules = session.modules ?? {};
  const moduleEnabled = (name: string, fallback = true) => modules[name]?.enabled ?? fallback;

  // ---------- Trust visibility logic ----------
  const trustModuleEnabled = moduleEnabled("trust");
  const trust = (session.trust ?? trustFromEligibility ?? null) as Trust;
  const trustStatus = normalizeStatus(trust?.status);

  const hasApplied =
    eligibilityAction === "dashboard" ||
    session.hasAppliedTrust === true ||
    (!!trust && APPLIED_STATUSES.has(trustStatus));

  const showTrustReapply =
    trustModuleEnabled && (eligibilityAction === "reapply" || (!!trust && trustStatus === "rejected"));

  const showTrustLink = trustModuleEnabled && hasApplied;
  const showTrustApply = trustModuleEnabled && !showTrustLink && !showTrustReapply;

  const showFx = moduleEnabled("fx") && !!plan?.supports_fx;
  const showAdmin = user?.admin_role === "super";

  async function confirmAndLogout() {
    // Keep it simple and universal—native confirm for now.
    const ok = window.confirm("Are you sure you want to log out?");
    if (!ok) return;
    try {
      await apiLogout();
    } catch {}
    // Route to SPA login landing after server session is cleared
    window.location.href = "/app/login";
  }

  const items = useMemo(() => {
    const base = [
      { type: "link" as const, to: "/dashboard", label: "Dashboard" },
      { type: "link" as const, to: "/accounts", label: "Funding Activity" },
      { type: "link" as const, to: "/transfers", label: "Transfer Activity" },
      { type: "link" as const, to: "/transactions", label: "Transactions" },
      { type: "link" as const, to: "/recipients", label: "Recipients" },
      ...(showFx ? [{ type: "link" as const, to: "/fx/create", label: "FX Exchange" }] : []),
      { type: "link" as const, to: "/profile", label: "Profile" },
      { type: "external" as const, href: "https://payonow.com", label: "Debit Card" },
    ];

    const trustHref = trust?.id ? `/trusts/${trust.id}` : `/trust`;

    const extras = [
      ...(showTrustLink ? [{ type: "link" as const, to: trustHref, label: "My Trust" }] : []),
      ...(showTrustReapply
        ? [{ type: "link" as const, to: `/trusts/${trust?.id}/reapply`, label: "Reapply" }]
        : []),
      ...(showTrustApply
        ? [{ type: "link" as const, to: "/trust/apply", label: "Apply for Trust" }]
        : []),
      ...(showAdmin ? [{ type: "link" as const, to: "/admin", label: "Admin Area" }] : []),
      { type: "action" as const, onClick: confirmAndLogout, label: "Logout" },
    ];
    return [...base, ...extras];
  }, [showFx, showAdmin, showTrustLink, showTrustReapply, showTrustApply, trust?.id]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b">
        <div className="mx-auto w-full max-w-md px-4 h-14 flex items-center justify-between">
          <button
            className="inline-flex items-center justify-center p-2 rounded-lg hover:bg-gray-100"
            aria-label="Open menu"
            onClick={toggle}
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-current" fill="none">
              <path d="M4 6h16M4 12h16M4 18h16" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <Link to="/dashboard" className="inline-flex items-center">
            <img src={branding.theme.logo_url} alt={branding.product_name} className="h-7" />
          </Link>
          <span className="w-9" />
        </div>
      </header>

      {/* Mobile menu */}
      <MobileMenu open={open} close={close} items={items} />

      {/* Content */}
      <main className="flex-1 mx-auto max-w-md w-full p-4 pb-24">
  <Outlet />
</main>

      {/* ---- Bottom tab bar ---- */}
      <nav className="app-tabbar fixed bottom-0 inset-x-0 z-40 border-t bg-white">
        <div className="mx-auto max-w-md flex">
          <MobileTab to="/dashboard" label="Dashboard" icon="dashboard" />
          <MobileTab to="/accounts" label="Funding" icon="wallet" />
          <MobileTab to="/transfers" label="Transfers" icon="send" />
          {trustModuleEnabled && <MobileTab to="/trusts" label="Trust" icon="shield" />}
        </div>
      </nav>
    </div>
  );
}

/** ---- Bottom tab component ---- */
function MobileTab({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: "dashboard" | "wallet" | "send" | "user" | "shield";
}) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <NavLink
      to={to}
      className={`flex flex-col items-center justify-center gap-1 flex-1 py-2 text-[11px] ${
        isActive ? "text-blue-700" : "text-gray-500"
      }`}
      aria-current={isActive ? "page" : undefined}
    >
      <span
        className={`rounded-md px-2 py-1 flex items-center justify-center ${
          isActive ? "bg-blue-50" : ""
        }`}
      >
        {icon === "dashboard" && <IconBar />}
        {icon === "wallet" && <IconWallet />}
        {icon === "send" && <IconSend />}
        {icon === "user" && <IconUser />}
        {icon === "shield" && <IconShield />}
      </span>
      <span className={isActive ? "font-medium" : ""}>{label}</span>
    </NavLink>
  );
}

/** ---- Tiny icons ---- */
function IconBar() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-current" fill="none">
      <path d="M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z" strokeWidth="1.6" />
    </svg>
  );
}
function IconWallet() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-current" fill="none">
      <path d="M3 7h18v10H3z" strokeWidth="1.6" />
      <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" strokeWidth="1.6" />
    </svg>
  );
}
function IconSend() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-current" fill="none">
      <path d="M22 2L11 13" strokeWidth="1.6" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="1.6" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-current" fill="none">
      <circle cx="12" cy="8" r="3.5" strokeWidth="1.6" />
      <path d="M5 20a7 7 0 0 1 14 0" strokeWidth="1.6" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 stroke-current" fill="none">
      <path
        d="M12 2l7 4v6c0 5.25-3.5 9.5-7 10-3.5-.5-7-4.75-7-10V6l7-4z"
        strokeWidth="1.6"
      />
    </svg>
  );
}
